import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import {
  ArrowRight,
  User,
  EnvelopeSimple,
  LockKey,
  Eye,
  EyeSlash,
  CircleNotch,
} from "@phosphor-icons/react";
import { registerSchema } from "@reactive-resume/dto";
import { usePasswordToggle } from "@reactive-resume/hooks";
import {
  Alert,
  AlertTitle,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router";
import type { z } from "zod";
import { motion } from "framer-motion";

import { useRegister } from "@/client/services/auth";
import { useFeatureFlags } from "@/client/services/feature";
import { SocialAuth } from "../_components/social-auth";

type FormValues = z.infer<typeof registerSchema>;

const ease = [0.22, 1, 0.36, 1] as const;

const fadeUp = (i: number) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: i * 0.055, duration: 0.4, ease },
});

const inputClassName = cn(
  "h-11 rounded-xl border border-[#C9D3E6]",
  "bg-white text-sm font-medium text-[#172033]",
  "caret-[#6687F0] shadow-sm",
  "placeholder:font-normal placeholder:text-[#98A2B3]",
  "transition-[border-color,box-shadow,background-color]",
  "hover:border-[#AEBDE0]",
  "focus-visible:border-[#6687F0]",
  "focus-visible:bg-white",
  "focus-visible:ring-2 focus-visible:ring-[#6687F0]/20",
  "",
  " ",
  "",
  "",
  "",
);

export const RegisterPage = () => {
  const navigate = useNavigate();
  const { flags } = useFeatureFlags();
  const { register, loading } = useRegister();
  const [showPwd, setShowPwd] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);
  usePasswordToggle(formRef);

  const form = useForm<FormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      locale: "fr-FR",
    },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      await register(data);
      void navigate("/client/user-space/resumes");
    } catch {
      form.setValue("password", "");
    }
  };

  return (
    <>
      <Helmet>
        <title>{t`Créer un compte`} — Mon CV Pro</title>
      </Helmet>

      {/* Eyebrow */}
      <motion.p
        {...fadeUp(0)}
        className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[0.2em] text-[#3ecf8e]"
      >
        REJOIGNEZ MON CV PRO
      </motion.p>

      {/* Titre */}
      <motion.h1
        {...fadeUp(1)}
        className="mb-1 text-[1.85rem] font-extrabold leading-[1.18] tracking-[-0.025em] text-gray-900"
      >
        Créez votre compte
      </motion.h1>

      {/* Sous-titre */}
      <motion.p {...fadeUp(2)} className="mb-5 text-[0.84rem] leading-relaxed text-gray-400">
        Construisez votre profil professionnel et donnez plus de visibilité à votre parcours.
      </motion.p>

      {flags.isSignupsDisabled && (
        <Alert variant="error" className="mb-4 rounded-xl">
          <AlertTitle>{t`Signups are currently disabled by the administrator.`}</AlertTitle>
        </Alert>
      )}

      <div className={cn(flags.isSignupsDisabled && "pointer-events-none select-none blur-sm")}>
        {/* Google / Social */}
        <motion.div {...fadeUp(3)}>
          <SocialAuth />
        </motion.div>

        <Form {...form}>
          <form
            ref={formRef}
            className="flex flex-col gap-y-3.5"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            {/* Nom */}
            <motion.div {...fadeUp(4)}>
              <FormField
                name="name"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className="text-[0.78rem] font-semibold text-gray-600">
                      {t`Nom`}
                    </FormLabel>

                    <FormControl>
                      <div className="relative">
                        <User
                          size={16}
                          className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-[#667085]"
                        />

                        <Input
                          autoComplete="name"
                          placeholder={t`Votre nom`}
                          className={cn(inputClassName, "pl-10")}
                          {...field}
                        />
                      </div>
                    </FormControl>

                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </motion.div>

            {/* Email */}
            <motion.div {...fadeUp(5)}>
              <FormField
                name="email"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className="text-[0.78rem] font-semibold text-gray-600">
                      {t`Adresse e-mail`}
                    </FormLabel>

                    <FormControl>
                      <div className="relative">
                        <EnvelopeSimple
                          size={16}
                          className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-[#667085]"
                        />

                        <Input
                          type="email"
                          autoComplete="email"
                          placeholder="exemple@domaine.com"
                          className={cn(inputClassName, "pl-10")}
                          {...field}
                        />
                      </div>
                    </FormControl>

                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </motion.div>

            {/* Mot de passe */}
            <motion.div {...fadeUp(6)}>
              <FormField
                name="password"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className="text-[0.78rem] font-semibold text-gray-600">
                      {t`Mot de passe`}
                    </FormLabel>

                    <FormControl>
                      <div className="relative">
                        <LockKey
                          size={16}
                          className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-[#667085]"
                        />

                        <Input
                          type={showPwd ? "text" : "password"}
                          placeholder={t`Créez un mot de passe sécurisé`}
                          className={cn(inputClassName, "pl-10 pr-11")}
                          {...field}
                        />

                        <button
                          type="button"
                          onClick={() => setShowPwd((v) => !v)}
                          className="absolute right-3.5 top-1/2 z-10 -translate-y-1/2 text-[#667085] transition-colors hover:text-[#465BBD]"
                          tabIndex={-1}
                          aria-label={showPwd ? "Masquer" : "Afficher"}
                        >
                          {showPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </FormControl>

                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </motion.div>

            {/* Bouton Continuer */}
            <motion.div {...fadeUp(7)} className="pt-0.5">
              <motion.div whileHover={{ scale: 1.012 }} whileTap={{ scale: 0.988 }}>
                <Button
                  type="submit"
                  disabled={loading}
                  className="group flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-[#472cb3] text-[0.92rem] font-semibold text-white shadow-[0_4px_16px_rgba(71,44,179,0.3)] transition-all duration-200 hover:bg-[#3a22a0] active:bg-[#311b8a]"
                >
                  {loading ? (
                    <CircleNotch size={16} weight="bold" className="animate-spin" />
                  ) : (
                    <>
                      {t`Continuer`}

                      <ArrowRight
                        size={16}
                        weight="bold"
                        className="transition-transform group-hover:translate-x-0.5"
                      />
                    </>
                  )}
                </Button>
              </motion.div>
            </motion.div>

            {/* Séparateur */}
            <motion.div {...fadeUp(8)} className="flex items-center gap-3">
              <div className="h-px flex-1 bg-gray-100" />

              <span className="text-[0.74rem] text-gray-400">{t`Vous avez déjà un compte ?`}</span>

              <div className="h-px flex-1 bg-gray-100" />
            </motion.div>

            {/* Se connecter */}
            <motion.div {...fadeUp(9)}>
              <motion.div whileHover={{ scale: 1.012 }} whileTap={{ scale: 0.988 }}>
                <Button
                  asChild
                  variant="outline"
                  className="h-12 w-full rounded-2xl border-2 border-[#472cb3] bg-white text-[0.92rem] font-semibold text-[#472cb3] transition-all duration-200 hover:bg-[#f5f2ff]"
                >
                  <Link to="/client/auth/login">{t`Se connecter`}</Link>
                </Button>
              </motion.div>
            </motion.div>
          </form>
        </Form>
      </div>
    </>
  );
};
